import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, CreditCardIcon, CalendarIcon, ClockIcon, FileTextIcon, UploadIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, TrashIcon, InfoIcon, HelpCircleIcon, XIcon as CloseIcon, ChevronLeftIcon, ChevronRightIcon, PenIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, MoreVerticalIcon, PinIcon, LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason, EvidenceDocument } from '../types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface RelatedOrder {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  createdTime: string;
  status: string;
  matchingCriteria: number;
  totalCriteria: number;
}
interface MatchingCriteriaDetail {
  category: string;
  subtitle: string;
  items: {
    label: string;
    matched: boolean;
  }[];
}
export function DisputeDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [activeTab, setActiveTab] = useState<'payment' | 'related'>('payment');
  const [selectedOrder, setSelectedOrder] = useState<RelatedOrder | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMatchingCriteriaTooltip, setShowMatchingCriteriaTooltip] = useState(false);
  const [showReasonTooltip, setShowReasonTooltip] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showChallengeFlow, setShowChallengeFlow] = useState(false);
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  const [showChallengeDetails, setShowChallengeDetails] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  // State to track if dispute has been accepted
  const [isAccepted, setIsAccepted] = useState(false);
  // State to track if dispute has been challenged (separate from derived isChallenged)
  const [hasBeenChallenged, setHasBeenChallenged] = useState(false);
  // State for adding notes
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [merchantNotes, setMerchantNotes] = useState<Array<{
    id: string;
    timestamp: string;
    description: string;
    status: 'note';
    author: string;
    isPinned?: boolean;
  }>>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [showNoteMenu, setShowNoteMenu] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  // Check if returning from challenge submission
  useEffect(() => {
    if (location.state?.challenged) {
      setHasBeenChallenged(true);
    }
  }, [location.state]);
  // Close note menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNoteMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.note-menu-container')) {
          setShowNoteMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNoteMenu]);
  const rowsPerPage = 5;
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeClick = () => {
    navigate(`/disputes/${id}/challenge`);
  };
  const handleViewChallengeDetails = () => {
    setShowChallengeDetails(true);
  };
  const handleChallengeConfirm = () => {
    setShowChallengeModal(false);
    setShowChallengeFlow(true);
    setChallengeStep('reason');
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    // Check if user has provided no evidence at all
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleCloseChallengeFlow = () => {
    setShowChallengeFlow(false);
    setChallengeStep('reason');
    setSelectedChallengeReason('purchasing_history');
    setSelectedProductType('');
    setNoEvidenceChecked(false);
    setNoOrderEvidenceChecked(false);
    setNoCustomerEvidenceChecked(false);
    setNoOtherEvidenceChecked(false);
    setShowNoEvidenceWarning(false);
    setReviewConfirmChecked(false);
  };
  const handleAcceptClick = () => {
    setShowAcceptModal(true);
  };
  const handleAcceptConfirm = () => {
    setIsAccepted(true);
    setShowAcceptModal(false);
  };
  const handleAddNote = () => {
    if (noteText.trim()) {
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: new Date().toISOString(),
        description: noteText.trim(),
        status: 'note' as const,
        author: 'Eng Han Ng',
        isPinned: false
      };
      setMerchantNotes([newNote, ...merchantNotes]);
      setNoteText('');
      setShowAddNote(false);
    }
  };
  const handleEditNote = (noteId: string) => {
    const note = merchantNotes.find(n => n.id === noteId);
    if (note) {
      setEditingNoteId(noteId);
      setEditingNoteText(note.description);
      setShowNoteMenu(null);
    }
  };
  const handleSaveEdit = (noteId: string) => {
    if (editingNoteText.trim()) {
      setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
        ...note,
        description: editingNoteText.trim()
      } : note));
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };
  const handleDeleteNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.filter(note => note.id !== noteId));
    setShowNoteMenu(null);
  };
  const handlePinNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
      ...note,
      isPinned: !note.isPinned
    } : note));
    setShowNoteMenu(null);
  };
  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedOrderId(orderId);
      setTimeout(() => setCopiedOrderId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  // Mock related orders data
  const relatedOrders: RelatedOrder[] = [{
    id: '1',
    orderId: '8b89fb9a-3fce-4aea-956f-49aa8d033caa',
    amount: 10000,
    currency: 'MYR',
    createdTime: '2025-07-06 09:01:43 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '2',
    orderId: 'bcd5898f-5af4-4bb8-99ee-6f2a308d799b',
    amount: 10000,
    currency: 'MYR',
    createdTime: '2025-07-06 09:01:41 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '3',
    orderId: '4086dc8d-fb0b-44d0-8e8c-cf018f0c778d',
    amount: 10000,
    currency: 'MYR',
    createdTime: '2025-07-06 08:49:46 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '4',
    orderId: 'a5cf0edf-9412-4ffe-9275-b8914ba25e25',
    amount: 10000,
    currency: 'MYR',
    createdTime: '2025-07-06 08:49:33 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '5',
    orderId: '6d92fede-b0c8-4efd-b633-230703873 56f',
    amount: 10000,
    currency: 'MYR',
    createdTime: '2025-07-06 08:49:31 UTC+0800',
    status: 'Completed',
    matchingCriteria: 7,
    totalCriteria: 7
  }];
  const matchingCriteriaDetails: MatchingCriteriaDetail[] = [{
    category: 'Customer information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: 'IP address',
      matched: true
    }, {
      label: 'Device ID / fingerprint',
      matched: true
    }, {
      label: 'Shipping address',
      matched: true
    }, {
      label: 'Email address',
      matched: false
    }]
  }, {
    category: 'Transaction information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: '120 - 365 days older',
      matched: true
    }, {
      label: 'Undisputed & not fraudulent',
      matched: true
    }]
  }, {
    category: 'Additional information',
    subtitle: '',
    items: [{
      label: 'Payment method',
      matched: true
    }]
  }];
  const totalPages = Math.ceil(relatedOrders.length / rowsPerPage);
  const paginatedOrders = relatedOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  // Find the dispute by ID using centralized data
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      // Return first dispute as fallback
      return allDisputes[0];
    }
    return found;
  }, [id, allDisputes]);
  // Check if dispute is challenged - either from the dispute status or from navigation state
  const isChallenged = dispute.status === 'challenged' || location.state?.challenged === true || hasBeenChallenged;
  // Get effective status (use accepted if user just accepted it)
  const effectiveStatus = isAccepted ? 'accepted' : dispute.status;
  const paymentAmount = dispute.amount;
  const disputedAmount = dispute.amount;
  const disputeFee = Math.round(dispute.amount * 0.08);
  const netAmount = disputedAmount + disputeFee;
  const formatCurrency = (amount: number, currency: string = 'MYR') => {
    const value = (amount / 100).toLocaleString('en-MY', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currency === 'MYR' ? 'RM' : currency} ${value}`;
  };
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      requires_response: 'text-[#92400E] bg-[#FFEDD5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#7C2D12] bg-[#FFEDD5]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${styles[status]}`} data-id="element-5826">
        {labels[status]}
      </span>;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getReasonDescription = (reason: DisputeReason): string => {
    const descriptions: Record<DisputeReason, string> = {
      duplicate: 'The customer claims this is a duplicate charge',
      fraudulent: 'The customer claims the transaction was fraudulent',
      subscription_canceled: 'The customer claims they canceled their subscription',
      product_unacceptable: 'The customer claims the product was unacceptable',
      product_not_received: 'The customer claims they did not receive the product',
      unrecognized: 'The customer does not recognize the transaction',
      credit_not_processed: 'The customer claims a credit was not processed',
      incorrect_account_details: 'The customer claims incorrect account details were used',
      insufficient_funds: 'The customer claims they had insufficient funds',
      bank_cannot_process: 'The bank cannot process the transaction',
      debit_not_authorized: 'The customer claims the debit was not authorized',
      general: 'General dispute reason',
      customer_initiated: 'Customer initiated the dispute',
      check_returned: 'The check was returned',
      noncompliant: 'The transaction was noncompliant'
    };
    return descriptions[reason] || 'Dispute reason not specified';
  };
  const getReasonLabel = (reason: DisputeReason): string => {
    const labels: Record<DisputeReason, string> = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentSource = () => {
    // In a real app, this would come from the dispute data
    // For now, we'll use a simple logic based on dispute ID
    const sources = ['Payment Link', 'QR Code', 'Invoice', 'Tap to Phone', 'POS Terminal'];
    const sourceIcons = [LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon];
    const index = parseInt(dispute.id) % sources.length;
    return {
      name: sources[index],
      Icon: sourceIcons[index]
    };
  };
  const hoursRemaining = getHoursRemaining(dispute.dueDate);
  const reversedLifecycle = useMemo(() => {
    const lifecycle = [...dispute.lifecycle];
    if (isChallenged) {
      lifecycle.push({
        id: '3',
        timestamp: new Date().toISOString(),
        status: 'challenged' as DisputeStatus,
        description: 'Dispute challenged with supporting evidence'
      });
    }
    if (isAccepted) {
      lifecycle.push({
        id: String(lifecycle.length + 1),
        timestamp: new Date().toISOString(),
        status: 'accepted' as DisputeStatus,
        description: 'Dispute accepted by merchant'
      });
    }
    // Separate pinned and unpinned notes
    const pinnedNotes = merchantNotes.filter(note => note.isPinned);
    const unpinnedNotes = merchantNotes.filter(note => !note.isPinned);
    // Sort unpinned notes by timestamp
    const sortedUnpinnedNotes = unpinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Combine: pinned notes first (sorted by timestamp), then lifecycle events and unpinned notes
    const allEvents = [...lifecycle, ...sortedUnpinnedNotes];
    const sortedEvents = allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Add pinned notes at the top
    return [...pinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()), ...sortedEvents];
  }, [dispute.lifecycle, isChallenged, isAccepted, merchantNotes]);
  // Get evidence recommendations using the centralized utility
  const evidenceRecs = useMemo(() => {
    // LOGGING: Start of evidence recommendation lookup
    console.group('🔍 Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand (raw):', dispute.cardBrand);
    console.log('Network Reason Code (raw):', dispute.networkReasonCode);
    console.log('Dispute Reason:', dispute.reason);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  // If challenge flow is active, show full-screen challenge interface
  if (showChallengeFlow) {
    return <div className="min-h-screen bg-white" data-id="element-5827">
        {/* Header */}
        <div className="border-b border-gray-200" data-id="element-5828">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-5829">
            <div className="flex items-center gap-3" data-id="element-5830">
              <div className="flex items-center gap-2" data-id="element-5831">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5832">
                  <path d="M3 7L12 2L21 7L12 12L3 7Z" fill="#FF6B35" stroke="#FF6B35" strokeWidth="2" strokeLinejoin="round" data-id="element-5833" />
                  <path d="M3 12L12 17L21 12" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5834" />
                  <path d="M3 17L12 22L21 17" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5835" />
                </svg>
                <span className="text-lg font-semibold" data-id="element-5836">Airwallex</span>
              </div>
              <span className="text-gray-400" data-id="element-5837">|</span>
              <span className="text-gray-700" data-id="element-5838">Challenge dispute</span>
            </div>
            <button onClick={handleCloseChallengeFlow} className="text-gray-400 hover:text-gray-600" data-id="element-5839">
              <CloseIcon size={24} data-id="element-5840" />
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-5841">
          <div className="flex gap-8" data-id="element-5842">
            {/* Left Sidebar */}
            <div className="w-64 flex-shrink-0" data-id="element-5843">
              <div className="space-y-1" data-id="element-5844">
                <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${expandedSections.challengeDetails ? 'bg-gray-100' : ''}`} onClick={() => toggleSection('challengeDetails')} data-id="element-5845">
                  <span className="font-medium" data-id="element-5846">Challenge details</span>
                  {expandedSections.challengeDetails ? <ChevronUpIcon size={16} data-id="element-5847" /> : <ChevronDownIcon size={16} data-id="element-5848" />}
                </button>
                {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-5849">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-5850">
                      {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5851" />}
                      Challenge reason
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5852">
                      {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5853" />}
                      Supporting evidence
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-5854">
                  <span data-id="element-5855">Transaction details</span>
                  {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-5856" /> : <ChevronDownIcon size={16} data-id="element-5857" />}
                </button>
                {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-5858">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5859">
                      {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5860" />}
                      Order details
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5861">
                      {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5862" />}
                      Customer details
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-5863">
                  <span data-id="element-5864">Other evidence</span>
                  {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-5865" /> : <ChevronDownIcon size={16} data-id="element-5866" />}
                </button>
                {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-5867">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5868">
                      {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5869" />}
                      Other supporting documents
                    </button>
                  </div>}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-5870">
              {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-5871">
                  <div className="flex items-start justify-between mb-6" data-id="element-5872">
                    <div data-id="element-5873">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5874">
                        Challenge reason
                      </h1>
                      <p className="text-gray-600" data-id="element-5875">
                        Your responses will help us recommend the best evidence
                        to submit for your challenge.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5876">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5877">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5878">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5879">
                        <div className="text-gray-500" data-id="element-5880">Dispute amount</div>
                        <div className="font-medium" data-id="element-5881">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5882">
                        <div className="text-gray-500" data-id="element-5883">Response due</div>
                        <div className="font-medium" data-id="element-5884">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5885">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5886">
                    <div data-id="element-5887">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-5888">
                        Why are you challenging the dispute?
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-5889">
                        Choose the closest option.
                      </p>
                      <div className="space-y-3" data-id="element-5890">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5891">
                          <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5892" />
                          <span className="text-sm text-gray-900" data-id="element-5893">
                            The customer withdrew the dispute
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5894">
                          <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5895" />
                          <span className="text-sm text-gray-900" data-id="element-5896">
                            The customer has already been refunded
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border-2 border-blue-600 bg-blue-50 rounded-lg cursor-pointer" data-id="element-5897">
                          <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5898" />
                          <div className="flex-1" data-id="element-5899">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5900">
                              The customer has a purchasing history with me
                            </div>
                            {selectedChallengeReason === 'purchasing_history' && <div className="mt-3 p-3 bg-white rounded border border-blue-200" data-id="element-5901">
                                <p className="text-sm text-gray-700" data-id="element-5902">
                                  We recommend this reason as we have identified
                                  transactions that prove the customer has a
                                  purchasing history with you.
                                </p>
                              </div>}
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5903">
                          <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5904" />
                          <span className="text-sm text-gray-900" data-id="element-5905">
                            Other reasons
                          </span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-5906">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-5907">
                        What type of product/service was the disputed payment
                        for?
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-5908">
                        Choose the closest option.
                      </p>
                      <div className="space-y-3" data-id="element-5909">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5910">
                          <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5911" />
                          <div data-id="element-5912">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5913">
                              Physical product
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5914">
                              Physical goods to be delivered to the customer,
                              e.g. clothes, accessories, electronic gadgets
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5915">
                          <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5916" />
                          <div data-id="element-5917">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5918">
                              Digital product or service
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5919">
                              Purchased access to software, digital files or
                              experiences, e.g. mobile apps, music service,
                              digital tickets
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5920">
                          <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5921" />
                          <div data-id="element-5922">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5923">
                              Offline service
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5924">
                              Labour or assistant services provided either at a
                              specific location or remotely, e.g.
                              transportation, home servicing
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5925">
                          <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5926" />
                          <div data-id="element-5927">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5928">
                              Reservation or booking
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5929">
                              Services related to reservations or bookings, e.g.
                              flight tickets, hotel booking
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5930">
                          <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5931" />
                          <span className="text-sm text-gray-900" data-id="element-5932">Other</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5933">
                    <button onClick={handleCloseChallengeFlow} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5934">
                      Cancel
                    </button>
                    <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5935">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-5936">
                  <div className="flex items-start justify-between mb-6" data-id="element-5937">
                    <div data-id="element-5938">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5939">
                        {evidenceRecs.title}
                      </h1>
                      <p className="text-gray-600" data-id="element-5940">
                        {evidenceRecs.description}
                        {evidenceRecs.learnMoreLink && <>
                            {' '}
                            <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-5941">
                              {evidenceRecs.learnMoreLink}
                            </a>
                          </>}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5942">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5943">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5944">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5945">
                        <div className="text-gray-500" data-id="element-5946">Dispute amount</div>
                        <div className="font-medium" data-id="element-5947">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5948">
                        <div className="text-gray-500" data-id="element-5949">Response due</div>
                        <div className="font-medium" data-id="element-5950">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5951">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-5952">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5953">
                      Provide supporting evidence
                    </h3>
                    {/* LOGGING: Verify evidence is being rendered */}
                    {console.log('🎨 Rendering evidence step with:', {
                  title: evidenceRecs.title,
                  recommendationsCount: evidenceRecs.recommendations.length,
                  firstRecommendation: evidenceRecs.recommendations[0]
                })}
                    {dispute.networkReasonCode === '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-5954">
                        For Visa 13.2 (Subscription Canceled) disputes, we
                        recommend you provide the following evidence:
                      </p>}
                    {dispute.reason === 'subscription_canceled' && dispute.networkReasonCode !== '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-5955">
                          For subscription cancellation disputes, we recommend
                          you provide the following evidence:
                        </p>}
                    {dispute.reason !== 'subscription_canceled' && <p className="text-sm text-gray-600 mb-4" data-id="element-5956">
                        Based on your challenge reason, we recommend providing:
                      </p>}
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-5957">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-5958">
                          <strong data-id="element-5959">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                    <p className="text-sm text-gray-500" data-id="element-5960">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-5961" />
                            {requirements.language}
                            <br data-id="element-5962" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  <div className="mb-6" data-id="element-5963">
                    <button disabled={noEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5964">
                      <UploadIcon size={16} data-id="element-5965" />
                      Upload file
                    </button>
                  </div>

                  <div className="mb-6" data-id="element-5966">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5967" />
                  </div>

                  <div className="mb-6" data-id="element-5968">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5969">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5970" />
                      <span className="text-sm text-gray-900" data-id="element-5971">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5972">
                    <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5973">
                      Back
                    </button>
                    <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5974">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-5975">
                  <div className="flex items-start justify-between mb-6" data-id="element-5976">
                    <div data-id="element-5977">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5978">
                        Order details
                      </h1>
                      <p className="text-gray-600" data-id="element-5979">
                        Please provide as much information as possible about the
                        goods/service the payment was disputed for. This will
                        allow the issuer to verify that the evidence you
                        provided corresponds to the transaction disputed by the
                        customer.
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-5980">
                        The details below have been pre-filled from transaction
                        data. Please review and edit the information if needed.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5981">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5982">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5983">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5984">
                        <div className="text-gray-500" data-id="element-5985">Dispute amount</div>
                        <div className="font-medium" data-id="element-5986">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5987">
                        <div className="text-gray-500" data-id="element-5988">Response due</div>
                        <div className="font-medium" data-id="element-5989">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5990">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5991">
                    <div data-id="element-5992">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5993">
                        Product details
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-5994">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5995">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5996" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5997">
                        <span data-id="element-5998">IPHONE7 * 5, 100.01 MYR</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5999">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6000">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6001" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6002">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6003">
                        Shipping address
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-6004">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6005">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6006" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6007">
                        <span data-id="element-6008">
                          Pudong District, Shanghai, Shanghai, CN, 291201
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6009">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6010">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6011" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6012">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6013">
                        Shipping carrier
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6014">
                        <span data-id="element-6015">sameday</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6016">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6017">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6018" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6019">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6020">
                        Provide additional supporting evidence
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6021">
                        As the payment was for a{' '}
                        <span className="font-medium" data-id="element-6022">Physical product</span>,
                        we recommend you to also provide the following
                        information:
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-6023">
                        <li data-id="element-6024">Shipping date</li>
                        <li data-id="element-6025">Shipping tracking number</li>
                      </ul>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6026">
                        You can upload any document containing such information
                        (e.g. invoices, shipping documentation, delivery
                        confirmation or shipping receipts) below.
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-6027">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-6028" />
                              {requirements.language}
                              <br data-id="element-6029" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-6030">
                      <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6031">
                        <UploadIcon size={16} data-id="element-6032" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-6033">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6034" />
                    </div>

                    <div data-id="element-6035">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6036">
                        <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6037" />
                        <span className="text-sm text-gray-900" data-id="element-6038">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6039">
                    <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6040">
                      Back
                    </button>
                    <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6041">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-6042">
                  <div className="flex items-start justify-between mb-6" data-id="element-6043">
                    <div data-id="element-6044">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6045">
                        Customer details
                      </h1>
                      <p className="text-gray-600" data-id="element-6046">
                        Please provide as much information as possible about the
                        customer. This will allow the issuer to verify that the
                        evidence you provided corresponds to the customer
                        disputing the transaction.
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-6047">
                        The details below have been pre-filled from transaction
                        data. Please review and edit the information if needed.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6048">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6049">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6050">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6051">
                        <div className="text-gray-500" data-id="element-6052">Dispute amount</div>
                        <div className="font-medium" data-id="element-6053">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6054">
                        <div className="text-gray-500" data-id="element-6055">Response due</div>
                        <div className="font-medium" data-id="element-6056">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6057">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6058">
                    <div data-id="element-6059">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6060">
                        Customer name
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6061">
                        <span data-id="element-6062">Test name</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6063">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6064">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6065" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6066">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6067">
                        Customer email
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6068">
                        <span data-id="element-6069">jessica@airwallex.com</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6070">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6071">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6072" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6073">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6074">
                        Customer purchase IP
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6075">
                        <span data-id="element-6076">35.221.170.121</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6077">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6078">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6079" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6080">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6081">
                        Customer phone
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6082">
                        <span data-id="element-6083">+60 12 345 6789</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6084">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6085">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6086" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6087">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6088">
                        Customer billing address
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6089">
                        <span data-id="element-6090">
                          Pudong District, Shanghai, Shanghai, CN, 201304
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6091">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6092">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6093" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-6094">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6095">
                        Provide supporting evidence
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6096">
                        We recommend you to provide the following information
                        about the customer:
                      </p>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-6097">
                        You can upload any document containing such information
                        (e.g. invoices, receipts) below.
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-6098">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-6099" />
                              {requirements.language}
                              <br data-id="element-6100" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-6101">
                      <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6102">
                        <UploadIcon size={16} data-id="element-6103" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-6104">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6105" />
                    </div>

                    <div data-id="element-6106">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6107">
                        <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6108" />
                        <span className="text-sm text-gray-900" data-id="element-6109">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6110">
                    <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6111">
                      Back
                    </button>
                    <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6112">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-6113">
                  <div className="flex items-start justify-between mb-6" data-id="element-6114">
                    <div data-id="element-6115">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6116">
                        Other supporting documents
                      </h1>
                      <p className="text-gray-600" data-id="element-6117">
                        If you have any other document(s) that you believe is
                        valuable to assess the legitimacy of your challenge, you
                        can add it below.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6118">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6119">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6120">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6121">
                        <div className="text-gray-500" data-id="element-6122">Dispute amount</div>
                        <div className="font-medium" data-id="element-6123">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6124">
                        <div className="text-gray-500" data-id="element-6125">Response due</div>
                        <div className="font-medium" data-id="element-6126">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6127">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6128">
                    <div data-id="element-6129">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6130">
                        Provide supporting evidence
                      </h3>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-6131">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-6132" />
                              {requirements.language}
                              <br data-id="element-6133" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-6134">
                      <button disabled={noOtherEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6135">
                        <UploadIcon size={16} data-id="element-6136" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-6137">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6138" />
                    </div>

                    <div data-id="element-6139">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6140">
                        <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6141" />
                        <span className="text-sm text-gray-900" data-id="element-6142">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6143">
                    <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6144">
                      Back
                    </button>
                    <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6145">
                      Review
                    </button>
                  </div>
                </div>}

              {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-6146">
                  <div className="flex items-start justify-between mb-6" data-id="element-6147">
                    <div data-id="element-6148">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6149">
                        Review your challenge
                      </h1>
                      <p className="text-gray-600" data-id="element-6150">
                        Please confirm that the information you have provided is
                        correct before submission.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6151">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6152">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6153">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-6154">
                        <div className="text-gray-500" data-id="element-6155">Dispute amount</div>
                        <div className="font-medium" data-id="element-6156">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-6157">
                        <div className="text-gray-500" data-id="element-6158">Response due</div>
                        <div className="font-medium" data-id="element-6159">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-6160">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-6161">
                    {/* Challenge details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-6162">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6163">
                        Challenge details
                      </h3>
                      <div className="space-y-3" data-id="element-6164">
                        <div className="flex justify-between" data-id="element-6165">
                          <span className="text-sm text-gray-600" data-id="element-6166">
                            Reason for challenge
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6167">
                            The customer has a purchasing history with me
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6168">
                          <span className="text-sm text-gray-600" data-id="element-6169">
                            Type of product or service
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6170">
                            Physical product
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-6171">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6172">
                        Transaction details
                      </h3>
                      <div className="space-y-3" data-id="element-6173">
                        <div className="flex justify-between" data-id="element-6174">
                          <span className="text-sm text-gray-600" data-id="element-6175">
                            Product details
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6176">
                            IPHONE7 * 5, 100.01 MYR
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6177">
                          <span className="text-sm text-gray-600" data-id="element-6178">
                            Shipping address
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6179">
                            Pudong District, Shanghai, Shanghai, CN, 291201
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6180">
                          <span className="text-sm text-gray-600" data-id="element-6181">
                            Customer name
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6182">
                            Test name
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6183">
                          <span className="text-sm text-gray-600" data-id="element-6184">
                            Customer email
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6185">
                            jessica@airwallex.com
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6186">
                          <span className="text-sm text-gray-600" data-id="element-6187">
                            Customer purchase IP
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6188">
                            35.221.170.121
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6189">
                          <span className="text-sm text-gray-600" data-id="element-6190">
                            Customer phone
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6191">
                            +60 12 345 6789
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6192">
                          <span className="text-sm text-gray-600" data-id="element-6193">
                            Customer billing address
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-6194">
                            Pudong District, Shanghai, Shanghai, CN, 201304
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Warning if no evidence */}
                    {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-6195">
                        <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-6196" />
                        <p className="text-sm text-gray-900" data-id="element-6197">
                          Please attach at least one of the recommended evidence
                          types to proceed with the response.
                        </p>
                      </div>}

                    {/* Confirmation checkbox */}
                    <div data-id="element-6198">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6199">
                        <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6200" />
                        <span className="text-sm text-gray-900" data-id="element-6201">
                          I understand that the current evidence can only be
                          submitted once for the issuer's review, and cannot be
                          reused.
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6202">
                    <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6203">
                      Back
                    </button>
                    <button disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6204">
                      Submit
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* No Evidence Warning Modal */}
        {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6205">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6206">
              <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-6207">
                <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6208"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6209">
                &#8203;
              </span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6210">
                <div className="bg-white px-6 pt-6 pb-4" data-id="element-6211">
                  <div className="flex items-start gap-4" data-id="element-6212">
                    <div className="flex-shrink-0" data-id="element-6213">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6214">
                        <AlertTriangleIcon size={24} className="text-red-600" data-id="element-6215" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-6216">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-6217">
                        Are you sure you want to proceed without evidence?
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-6218">
                        Submitting a challenge without supporting evidence
                        significantly reduces your chances of winning the
                        dispute. We strongly recommend providing at least one
                        type of evidence to support your case.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-6219">
                  <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6220">
                    Go back and add evidence
                  </button>
                  <button onClick={() => {
                setShowNoEvidenceWarning(false);
                setChallengeStep('review');
              }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700" data-id="element-6221">
                    Proceed anyway
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-6222">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-6223">
        <button onClick={() => navigate('/disputes')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-6224">
          <ArrowLeftIcon size={20} data-id="element-6225" />
          Back to Disputes
        </button>
      </div>

      {/* Dispute Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6226">
        <div className="flex items-start justify-between mb-6" data-id="element-6227">
          <div className="flex items-center gap-3" data-id="element-6228">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6229">Dispute</h1>
            {getStatusBadge(effectiveStatus)}
          </div>
          <button onClick={() => setShowHelpModal(true)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-6230">
            {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
            <HelpCircleIcon size={16} data-id="element-6231" />
          </button>
        </div>

        <div className="mb-6" data-id="element-6232">
          <div className="text-sm text-gray-500" data-id="element-6233">
            Dispute ID: {dispute.disputeId}
          </div>
        </div>

        {isChallenged && <div className="mb-6 p-5 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-6234">
            <div className="flex items-start gap-4" data-id="element-6235">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-6236">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6237" />
              </div>
              <div className="flex-1" data-id="element-6238">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6239">
                  Challenge submitted
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-6240">
                  Your challenge has been submitted and is being reviewed by the
                  issuing bank. You will be notified of any updates.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors" data-id="element-6241">
                  <FileTextIcon size={18} data-id="element-6242" />
                  View challenge details
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'won' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-6243">
            <div className="flex items-start gap-4" data-id="element-6244">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-6245">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6246" />
              </div>
              <div className="flex-1" data-id="element-6247">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6248">
                  Dispute won
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-6249">
                  The issuing bank has reviewed your evidence and ruled in your
                  favor. The disputed amount has been credited back to your
                  account.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm transition-colors" data-id="element-6250">
                  <FileTextIcon size={18} data-id="element-6251" />
                  View your submitted evidence
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'reversed' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-6252">
            <div className="flex items-start gap-4" data-id="element-6253">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-6254">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6255" />
              </div>
              <div className="flex-1" data-id="element-6256">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6257">
                  Dispute reversed
                </div>
                <div className="text-sm text-gray-700" data-id="element-6258">
                  The cardholder has withdrawn their dispute. The disputed
                  amount has been credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'accepted' && <div className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-lg" data-id="element-6259">
            <div className="flex items-start gap-4" data-id="element-6260">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0" data-id="element-6261">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6262" />
              </div>
              <div className="flex-1" data-id="element-6263">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6264">
                  Dispute accepted
                </div>
                <div className="text-sm text-gray-700" data-id="element-6265">
                  You have accepted this dispute. The disputed amount will not
                  be credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {isAccepted && <div className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-lg" data-id="element-6266">
            <div className="flex items-start gap-4" data-id="element-6267">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0" data-id="element-6268">
                <CheckCircleIcon size={20} className="text-white" data-id="element-6269" />
              </div>
              <div className="flex-1" data-id="element-6270">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6271">
                  Dispute accepted
                </div>
                <div className="text-sm text-gray-700" data-id="element-6272">
                  You have accepted this dispute. The disputed amount will not
                  be credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'lost' && <div className="mb-6 p-5 bg-red-50 border-2 border-red-200 rounded-lg" data-id="element-6273">
            <div className="flex items-start gap-4" data-id="element-6274">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0" data-id="element-6275">
                <XCircleIcon size={20} className="text-white" data-id="element-6276" />
              </div>
              <div className="flex-1" data-id="element-6277">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-6278">
                  Dispute lost
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-6279">
                  The issuing bank has reviewed your evidence and ruled in favor
                  of the cardholder. The disputed amount will not be credited
                  back to your account.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm transition-colors" data-id="element-6280">
                  <FileTextIcon size={18} data-id="element-6281" />
                  View your submitted evidence
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-6282">
          <div data-id="element-6283">
            <div className="text-sm text-gray-500 mb-1" data-id="element-6284">Dispute amount</div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-6285">
              {formatCurrency(dispute.amount)}
            </div>
          </div>
          <div data-id="element-6286">
            <div className="text-sm text-gray-500 mb-1" data-id="element-6287">Response due</div>
            <div className="text-base font-medium text-gray-900" data-id="element-6288">
              {formatDateTime(dispute.dueDate)}
            </div>
            {hoursRemaining > 0 && hoursRemaining <= 24 && !isChallenged && <div className="text-sm text-red-600 font-medium" data-id="element-6289">
                Due in {hoursRemaining}{' '}
                {hoursRemaining === 1 ? 'hour' : 'hours'}
              </div>}
          </div>
        </div>

        {showAlert && effectiveStatus === 'requires_response' && !isChallenged && !isAccepted && <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-id="element-6290">
              <div className="flex items-start justify-between" data-id="element-6291">
                <div className="flex items-start gap-3" data-id="element-6292">
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-6293">
                    <InfoIcon size={14} className="text-white" data-id="element-6294" />
                  </div>
                  <div data-id="element-6295">
                    <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-6296">
                      Challenge this dispute with customer's purchasing history.
                    </div>
                    <div className="text-sm text-gray-700" data-id="element-6297">
                      We have identified matching transactions that prove this
                      customer has a purchasing history with you (see{' '}
                      <button onClick={() => setActiveTab('related')} className="text-blue-600 hover:text-blue-700 underline" data-id="element-6298">
                        Related orders
                      </button>
                      ). You can challenge this dispute by providing additional
                      details about these matching transactions.
                    </div>
                  </div>
                </div>
                <button onClick={() => setShowAlert(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0" data-id="element-6299">
                  <CloseIcon size={18} data-id="element-6300" />
                </button>
              </div>
            </div>}

        {isChallenged && <div className="mb-6" data-id="element-6301">
            <button onClick={handleViewChallengeDetails} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6302">
              View challenge details
            </button>
          </div>}

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-6303">
          <div data-id="element-6304">
            <div className="text-gray-500 mb-1" data-id="element-6305">Reason</div>
            <div className="flex items-center gap-2" data-id="element-6306">
              <span className="font-medium text-gray-900" data-id="element-6307">
                {getReasonLabel(dispute.reason)}
              </span>
              <div className="relative inline-block" data-id="element-6308">
                <button onMouseEnter={() => setShowReasonTooltip(true)} onMouseLeave={() => setShowReasonTooltip(false)} className="inline-flex items-center" data-id="element-6309">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-6310" />
                </button>
                {showReasonTooltip && <div className="absolute z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-6311">
                    {getReasonDescription(dispute.reason)}
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-6312"></div>
                  </div>}
              </div>
            </div>
          </div>
          <div data-id="element-6313">
            <div className="text-gray-500 mb-1" data-id="element-6314">Network reason code</div>
            <div className="font-medium text-gray-900" data-id="element-6315">
              {dispute.networkReasonCode}
            </div>
          </div>
          <div data-id="element-6316">
            <div className="text-gray-500 mb-1" data-id="element-6317">Dispute stage</div>
            <div className="font-medium text-gray-900 capitalize" data-id="element-6318">
              {dispute.stage.replace(/_/g, ' ')}
            </div>
          </div>
          <div data-id="element-6319">
            <div className="text-gray-500 mb-1" data-id="element-6320">Dispute time</div>
            <div className="font-medium text-gray-900" data-id="element-6321">
              {formatDateTime(dispute.createdAt)}
            </div>
          </div>
          <div data-id="element-6322">
            <div className="text-gray-500 mb-1" data-id="element-6323">Transaction type</div>
            <div className="font-medium text-gray-900" data-id="element-6324">Payment</div>
          </div>
          <div data-id="element-6325">
            <div className="text-gray-500 mb-1" data-id="element-6326">Issuer comments</div>
            <div className="font-medium text-gray-900" data-id="element-6327">Recall</div>
          </div>
        </div>

        {dispute.status === 'requires_response' && !isChallenged && !isAccepted && <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200" data-id="element-6328">
              <button onClick={handleChallengeClick} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md" data-id="element-6329">
                Challenge
              </button>
              <button onClick={handleAcceptClick} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-6330">
                Accept
              </button>
            </div>}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-6331">
        <div className="border-b border-gray-200" data-id="element-6332">
          <nav className="flex gap-8 px-6" aria-label="Tabs" data-id="element-6333">
            <button onClick={() => setActiveTab('payment')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payment' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6334">
              Payment summary
            </button>
            <button onClick={() => setActiveTab('related')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'related' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6335">
              Related orders
            </button>
          </nav>
        </div>

        {/* Payment Summary Tab Content */}
        {activeTab === 'payment' && <div className="p-6" data-id="element-6336">
            <div className="space-y-4 text-sm" data-id="element-6337">
              <div data-id="element-6338">
                <div className="text-gray-500 mb-1" data-id="element-6339">Order ID</div>
                <div className="flex items-center gap-2" data-id="element-6340">
                  <span className="font-medium text-gray-900" data-id="element-6341">
                    {dispute.transactionId}
                  </span>
                  <div className="relative group" data-id="element-6342">
                    <button onClick={() => handleCopyOrderId(dispute.transactionId)} className="text-gray-400 hover:text-blue-600 transition-colors" title="Copy Order ID" data-id="element-6343">
                      {copiedOrderId === dispute.transactionId ? <CheckIcon size={16} className="text-green-600" data-id="element-6344" /> : <CopyIcon size={16} data-id="element-6345" />}
                    </button>
                    {copiedOrderId === dispute.transactionId && <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap" data-id="element-6346">
                        Copied!
                      </div>}
                  </div>
                </div>
              </div>
              <div data-id="element-6347">
                <div className="text-gray-500 mb-1" data-id="element-6348">Source</div>
                <div className="flex items-center gap-2" data-id="element-6349">
                  {(() => {
                const {
                  name,
                  Icon
                } = getPaymentSource();
                return <>
                        <Icon size={16} className="text-gray-600" data-id="element-6350" />
                        <span className="font-medium text-gray-900" data-id="element-6351">
                          {name}
                        </span>
                      </>;
              })()}
                </div>
              </div>
              <div data-id="element-6352">
                <div className="text-gray-500 mb-1" data-id="element-6353">Payment type</div>
                <div className="font-medium text-gray-900" data-id="element-6354">Debit card</div>
              </div>
              <div data-id="element-6355">
                <div className="text-gray-500 mb-1" data-id="element-6356">Payment method</div>
                <div className="flex items-center gap-2" data-id="element-6357">
                  <span className="font-medium text-gray-900" data-id="element-6358">
                    {dispute.cardBrand} •••• {dispute.cardLast4}
                  </span>
                </div>
              </div>
              <div data-id="element-6359">
                <div className="text-gray-500 mb-1" data-id="element-6360">Cardholder name</div>
                <div className="font-medium text-gray-900" data-id="element-6361">
                  {dispute.customerName}
                </div>
              </div>
              <div data-id="element-6362">
                <div className="text-gray-500 mb-1" data-id="element-6363">Customer purchase IP</div>
                <div className="font-medium text-gray-900" data-id="element-6364">35.221.170.121</div>
              </div>
              <div data-id="element-6365">
                <div className="text-gray-500 mb-1" data-id="element-6366">Customer phone</div>
                <div className="font-medium text-gray-900" data-id="element-6367">+60 12 345 6789</div>
              </div>
              <div data-id="element-6368">
                <div className="text-gray-500 mb-1" data-id="element-6369">Security checks</div>
                <div className="space-y-1" data-id="element-6370">
                  <div className="flex items-center gap-2 text-green-600" data-id="element-6371">
                    <CheckCircleIcon size={16} data-id="element-6372" />
                    <span data-id="element-6373">Card security code</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-6374">
                    <XCircleIcon size={16} data-id="element-6375" />
                    <span data-id="element-6376">3DS</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-6377">
                    <XCircleIcon size={16} data-id="element-6378" />
                    <span data-id="element-6379">Billing address verification</span>
                  </div>
                </div>
              </div>
              <a href="#" className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-block" data-id="element-6380">
                View payment details
              </a>
            </div>
          </div>}

        {/* Related Orders Tab Content */}
        {activeTab === 'related' && <div className="p-6" data-id="element-6381">
            <div className="mb-4 text-sm text-gray-600" data-id="element-6382">
              Customers may have a purchasing history with you, which could be
              used as evidence to challenge the claim that there is a fraudulent
              charge. To help you define this history, we have identified orders
              that qualify as{' '}
              <span className="font-medium" data-id="element-6383">
                matching transactions to the issuer
              </span>
              .{' '}
              <div className="relative inline-block" data-id="element-6384">
                <button onMouseEnter={() => setShowMatchingCriteriaTooltip(true)} onMouseLeave={() => setShowMatchingCriteriaTooltip(false)} className="inline-flex items-center" data-id="element-6385">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-6386" />
                </button>
                {showMatchingCriteriaTooltip && <div className="absolute z-10 w-96 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-6387">
                    <div className="space-y-2" data-id="element-6388">
                      <p data-id="element-6389">
                        To qualify as a matching transaction to the issuer, a
                        transaction must meet the following criteria:
                      </p>
                      <ul className="list-disc pl-4 space-y-1" data-id="element-6390">
                        <li data-id="element-6391">
                          At least two data elements (IP address, device ID /
                          fingerprint, shipping address, email address) must
                          match across all three transactions, and one of these
                          two must be the IP address or device ID or fingerprint
                        </li>
                        <li data-id="element-6392">
                          Between 120 and 365 days older than the date of the
                          disputed transaction
                        </li>
                        <li data-id="element-6393">
                          Undisputed, and not have been reported as fraudulent
                        </li>
                      </ul>
                      <p data-id="element-6394">
                        We also show you if the payment method matches, which
                        can be used to support your challenge.
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-6395"></div>
                  </div>}
              </div>
            </div>

            {/* Related Orders Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-6396">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-6397">
                <thead className="bg-gray-50" data-id="element-6398">
                  <tr data-id="element-6399">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6400">
                      Order ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6401">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6402">
                      Created Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6403">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6404">
                      Matching Criteria
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6405">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-gray-50" data-id="element-6406">
                      <td className="px-4 py-3" data-id="element-6407">
                        <div className="flex items-center gap-2" data-id="element-6408">
                          <span className="text-sm text-gray-900" data-id="element-6409">
                            {order.orderId}
                          </span>
                          <button className="text-blue-600 hover:text-blue-700" data-id="element-6410">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6411">
                              <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6412" />
                              <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6413" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-6414">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-6415">
                        {order.createdTime}
                      </td>
                      <td className="px-4 py-3" data-id="element-6416">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-6417">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-6418">
                        <button onClick={() => setSelectedOrder(order)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6419">
                          {order.matchingCriteria} of {order.totalCriteria}{' '}
                          criteria
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-6420">
                <div className="flex items-center gap-2" data-id="element-6421">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6422">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6423">
                      <path d="M11 12L7 8L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6424" />
                      <path d="M5 12L5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6425" />
                    </svg>
                  </button>
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6426">
                    <ChevronLeftIcon size={16} data-id="element-6427" />
                  </button>
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 text-sm rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`} data-id="element-6428">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6429">
                    <ChevronRightIcon size={16} data-id="element-6430" />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6431">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6432">
                      <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6433" />
                      <path d="M11 4L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6434" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-4" data-id="element-6435">
                  <div className="text-sm text-gray-700" data-id="element-6436">
                    Rows per page:{' '}
                    <span className="font-medium" data-id="element-6437">{rowsPerPage}</span>
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-6438">
                    {(currentPage - 1) * rowsPerPage + 1} -{' '}
                    {Math.min(currentPage * rowsPerPage, relatedOrders.length)}{' '}
                    of {relatedOrders.length}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Breakdown Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6439">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6440">
          Payment breakdown
        </h3>
        <div className="space-y-3 max-w-md" data-id="element-6441">
          <div className="flex items-center justify-between" data-id="element-6442">
            <div className="text-sm text-gray-600" data-id="element-6443">Payment amount</div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6444">
              {formatCurrency(paymentAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-6445">
            <div className="flex items-center gap-1" data-id="element-6446">
              <span className="text-sm text-gray-600" data-id="element-6447">
                Final disputed amount
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-6448" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6449">
              - {formatCurrency(disputedAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-6450">
            <div className="flex items-center gap-1" data-id="element-6451">
              <span className="text-sm text-gray-600" data-id="element-6452">Dispute fee</span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-6453" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6454">
              - {formatCurrency(disputeFee)}
            </div>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-6455">
            <div className="flex items-center justify-between" data-id="element-6456">
              <div className="text-sm font-semibold text-gray-900" data-id="element-6457">
                Net amount
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-6458">
                - {formatCurrency(netAmount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6459">
        <div className="flex items-center justify-between mb-4" data-id="element-6460">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-6461">
            Recent Activity
          </h3>
          <button onClick={() => setShowAddNote(!showAddNote)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md transition-colors" data-id="element-6462">
            <PenIcon size={16} data-id="element-6463" />
            Add note
          </button>
        </div>

        {/* Add Note Form */}
        {showAddNote && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-6464">
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note for internal reference..." rows={3} className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3" data-id="element-6465" />
            <div className="flex items-center justify-end gap-2" data-id="element-6466">
              <button onClick={() => {
            setShowAddNote(false);
            setNoteText('');
          }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6467">
                Cancel
              </button>
              <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6468">
                Add note
              </button>
            </div>
          </div>}

        <div className="space-y-4" data-id="element-6469">
          {reversedLifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-6470">
              <div className="flex flex-col items-center" data-id="element-6471">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${event.status === 'note' ? 'bg-purple-100' : index === 0 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-6472">
                  {event.status === 'note' ? <FileTextIcon size={16} className="text-purple-600" data-id="element-6473" /> : <ClockIcon size={16} className={index === 0 ? 'text-blue-600' : 'text-gray-400'} data-id="element-6474" />}
                </div>
                {index < reversedLifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-6475"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-6476">
                <div className="flex items-start justify-between gap-2" data-id="element-6477">
                  <div className="flex-1" data-id="element-6478">
                    {event.status === 'note' && <div className="flex items-center gap-2 mb-1" data-id="element-6479">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-6480">
                          {event.isPinned && <PinIcon size={12} data-id="element-6481" />}
                          Internal Note
                        </span>
                      </div>}
                    {editingNoteId === event.id ? <div className="space-y-2" data-id="element-6482">
                        <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-6483" />
                        <div className="flex items-center gap-2" data-id="element-6484">
                          <button onClick={() => handleSaveEdit(event.id)} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700" data-id="element-6485">
                            Save
                          </button>
                          <button onClick={handleCancelEdit} className="px-3 py-1 text-xs font-medium text-gray-700 hover:text-gray-900" data-id="element-6486">
                            Cancel
                          </button>
                        </div>
                      </div> : <>
                        <div className="text-sm font-medium text-gray-900" data-id="element-6487">
                          {event.description}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-6488">
                          {formatDateTime(event.timestamp)}
                          {event.status === 'note' && event.author && <span data-id="element-6489"> by {event.author}</span>}
                        </div>
                      </>}
                  </div>
                  {event.status === 'note' && editingNoteId !== event.id && <div className="relative note-menu-container" data-id="element-6490">
                      <button onClick={() => setShowNoteMenu(showNoteMenu === event.id ? null : event.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" data-id="element-6491">
                        <MoreVerticalIcon size={16} data-id="element-6492" />
                      </button>
                      {showNoteMenu === event.id && <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-6493">
                          <button onClick={() => handlePinNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-6494">
                            <PinIcon size={14} data-id="element-6495" />
                            {event.isPinned ? 'Unpin from top' : 'Pin to the top'}
                          </button>
                          <button onClick={() => handleEditNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-6496">
                            <PenIcon size={14} data-id="element-6497" />
                            Edit
                          </button>
                          <button onClick={() => handleDeleteNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-6498">
                            <TrashIcon size={14} data-id="element-6499" />
                            Delete
                          </button>
                        </div>}
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Challenge Modal */}
      {showChallengeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6500">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6501">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeModal(false)} data-id="element-6502">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6503"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6504">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6505">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6506">
                <div className="flex items-start justify-between mb-4" data-id="element-6507">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-6508">
                    You are challenging the dispute
                  </h2>
                  <button onClick={() => setShowChallengeModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-6509">
                    <CloseIcon size={20} data-id="element-6510" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-6511">
                  Here's a preview of what will happen next:
                </p>

                <div className="space-y-6" data-id="element-6512">
                  <div className="flex gap-4" data-id="element-6513">
                    <div className="flex flex-col items-center" data-id="element-6514">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-6515">
                        <PenIcon size={20} className="text-blue-600" data-id="element-6516" />
                      </div>
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-6517"></div>
                    </div>
                    <div className="flex-1 pb-6" data-id="element-6518">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6519">
                        You submit the challenge
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-6520">
                        We'll guide you to provide the most compelling evidence
                        to support your challenge.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4" data-id="element-6521">
                    <div className="flex flex-col items-center" data-id="element-6522">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-6523">
                        <div className="w-3 h-3 rounded-full border-2 border-gray-400" data-id="element-6524"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-6525">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6526">
                        Issuer reviews the evidence & decides on next steps
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-6527">
                        If the issuer is convinced by the submitted evidence,
                        the disputed amount will be credited back to you with
                        the next settlement batch. Please note that the
                        chargeback fee will not be credited to you.
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-6528">
                        If they are not convinced by the submitted evidence,
                        they will decline the evidence and the dispute will be
                        lost. No further action is possible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-6529">
                <button onClick={() => setShowChallengeModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6530">
                  Cancel
                </button>
                <button onClick={handleChallengeConfirm} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-6531">
                  Challenge
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Evidence Form Modal - keeping existing */}
      {showEvidenceForm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6532">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6533">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowEvidenceForm(false)} data-id="element-6534">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6535"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6536">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-6537">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-6538">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-6539">
                  Submit Evidence
                </h3>
                <div className="space-y-4" data-id="element-6540">
                  <div data-id="element-6541">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6542">
                      Customer Communication
                    </label>
                    <textarea rows={3} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Describe any communication with the customer..." data-id="element-6543" />
                  </div>
                  <div data-id="element-6544">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6545">
                      Shipping/Delivery Documentation
                    </label>
                    <textarea rows={2} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Tracking numbers, delivery confirmation, etc..." data-id="element-6546" />
                  </div>
                  <div data-id="element-6547">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6548">
                      Upload Supporting Documents
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-6549">
                      <UploadIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-6550" />
                      <div className="text-sm text-gray-600" data-id="element-6551">
                        Drag and drop files here, or click to browse
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-6552">
                        PDF, JPG, PNG up to 10MB each
                      </div>
                      <input type="file" multiple className="hidden" data-id="element-6553" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-6554">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-6555">
                  Submit Evidence
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-6556">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Matching Criteria Side Panel */}
      {selectedOrder && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-6557">
          <div className="absolute inset-0 overflow-hidden" data-id="element-6558">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedOrder(null)} data-id="element-6559"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-6560">
              <div className="w-screen max-w-md" data-id="element-6561">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto" data-id="element-6562">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200" data-id="element-6563">
                    <div className="flex items-center justify-between" data-id="element-6564">
                      <h2 className="text-lg font-semibold text-gray-900" data-id="element-6565">
                        Related Order
                      </h2>
                      <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600" data-id="element-6566">
                        <CloseIcon size={20} data-id="element-6567" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-6 py-4" data-id="element-6568">
                    {/* Order Status */}
                    <div className="mb-6" data-id="element-6569">
                      <div className="text-sm font-medium text-gray-900 mb-2" data-id="element-6570">
                        Order
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 uppercase" data-id="element-6571">
                        {selectedOrder.status}
                      </span>
                    </div>

                    {/* Order ID */}
                    <div className="mb-6" data-id="element-6572">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6573">Order ID</div>
                      <div className="flex items-center gap-2" data-id="element-6574">
                        <span className="text-sm font-medium text-gray-900" data-id="element-6575">
                          {selectedOrder.orderId}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6576">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6577">
                            <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6578" />
                            <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6579" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Matching Criteria */}
                    <div className="mb-6" data-id="element-6580">
                      <div className="flex items-center gap-2 mb-3" data-id="element-6581">
                        <h3 className="text-sm font-semibold text-gray-900" data-id="element-6582">
                          Matching criteria
                        </h3>
                        <InfoIcon size={14} className="text-gray-400" data-id="element-6583" />
                      </div>

                      <div className="space-y-6" data-id="element-6584">
                        {matchingCriteriaDetails.map((category, idx) => <div key={idx} data-id="element-6585">
                            <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-6586">
                              {category.category}
                            </div>
                            {category.subtitle && <div className="text-xs text-gray-500 mb-2" data-id="element-6587">
                                {category.subtitle}
                              </div>}
                            <div className="space-y-2" data-id="element-6588">
                              {category.items.map((item, itemIdx) => <div key={itemIdx} className="flex items-center gap-2" data-id="element-6589">
                                  {item.matched ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-6590" /> : <XCircleIcon size={16} className="text-red-600" data-id="element-6591" />}
                                  <span className="text-sm text-gray-700" data-id="element-6592">
                                    {item.label}
                                  </span>
                                </div>)}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="border-t border-gray-200 pt-6" data-id="element-6593">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-6594">
                        Payment summary
                      </h3>
                      <div className="space-y-3 text-sm" data-id="element-6595">
                        <div className="flex justify-between" data-id="element-6596">
                          <span className="text-gray-600" data-id="element-6597">
                            Payment created time
                          </span>
                          <span className="text-gray-900" data-id="element-6598">
                            2025-07-06
                            <br data-id="element-6599" />
                            09:01 UTC+0800
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6600">
                          <span className="text-gray-600" data-id="element-6601">Payment amount</span>
                          <span className="text-gray-900" data-id="element-6602">
                            {formatCurrency(selectedOrder.amount, selectedOrder.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6603">
                          <span className="text-gray-600" data-id="element-6604">Card type</span>
                          <span className="text-gray-900" data-id="element-6605">CREDIT</span>
                        </div>
                        <div className="flex justify-between" data-id="element-6606">
                          <span className="text-gray-600" data-id="element-6607">Payment method</span>
                          <div className="flex items-center gap-2" data-id="element-6608">
                            <span className="text-blue-600 font-medium" data-id="element-6609">
                              visa
                            </span>
                            <span className="text-gray-900" data-id="element-6610">
                              Visa •••• 9400
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between" data-id="element-6611">
                          <span className="text-gray-600" data-id="element-6612">Cardholder name</span>
                          <span className="text-gray-900" data-id="element-6613">Auto Testing</span>
                        </div>
                        <div data-id="element-6614">
                          <div className="text-gray-600 mb-2" data-id="element-6615">
                            Payment security check
                          </div>
                          <div className="space-y-1" data-id="element-6616">
                            <div className="flex items-center gap-2" data-id="element-6617">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-6618" />
                              <span className="text-gray-700 text-xs" data-id="element-6619">
                                Card security code
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-6620">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-6621" />
                              <span className="text-gray-700 text-xs" data-id="element-6622">3DS</span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-6623">
                              <XCircleIcon size={14} className="text-gray-400" data-id="element-6624" />
                              <span className="text-gray-700 text-xs" data-id="element-6625">
                                Billing address verification
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-6626">
                    <button onClick={() => setSelectedOrder(null)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6627">
                      Close
                    </button>
                    <button className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6628">
                      View payment details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Challenge Details Modal */}
      {showChallengeDetails && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6629">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6630">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeDetails(false)} data-id="element-6631">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6632"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6633">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-6634">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-6635">
                <div className="flex items-start justify-between mb-6" data-id="element-6636">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6637">
                    Challenge details
                  </h2>
                  <button onClick={() => setShowChallengeDetails(false)} className="text-gray-400 hover:text-gray-600" data-id="element-6638">
                    <CloseIcon size={24} data-id="element-6639" />
                  </button>
                </div>

                <div className="space-y-6" data-id="element-6640">
                  <div data-id="element-6641">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-6642">
                      Challenge details
                    </h3>
                    <div className="space-y-3" data-id="element-6643">
                      <div className="flex" data-id="element-6644">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6645">
                          Reason for challenge
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6646">
                          The customer has a purchasing history with me
                        </span>
                      </div>
                      <div className="flex" data-id="element-6647">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6648">
                          Type of product or service
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6649">
                          Physical product
                        </span>
                      </div>
                      <div className="flex" data-id="element-6650">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6651">
                          Supporting evidence
                        </span>
                        <div className="flex-1" data-id="element-6652">
                          <div className="flex items-center gap-3" data-id="element-6653">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-6654">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6655">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6656" />
                                <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6657" />
                              </svg>
                            </div>
                            <div className="flex-1" data-id="element-6658">
                              <div className="text-sm font-medium text-gray-900" data-id="element-6659">
                                OHMM-Cairo-Specs-She...9Jul25.pdf
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-6660">
                                1.04 MB
                              </div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-700" data-id="element-6661">
                              <EyeIcon size={18} data-id="element-6662" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-6663">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-6664">
                      Transaction details
                    </h3>
                    <div className="space-y-3" data-id="element-6665">
                      <div className="flex" data-id="element-6666">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6667">
                          Product details
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6668">
                          IPHONE7 * 5, {formatCurrency(dispute.amount)}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6669">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6670">
                          Shipping address
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6671">
                          Menara Maybank, Maybank Tower, 100 Jalan Tun Perak,
                          Bukit Bintang, 50050 Kuala Lumpur, Wilayah Persekutuan
                          Kuala Lumpur, Malaysia
                        </span>
                      </div>
                      <div className="flex" data-id="element-6672">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6673">
                          Customer name
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6674">Test name</span>
                      </div>
                      <div className="flex" data-id="element-6675">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6676">
                          Customer email
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6677">
                          customer@example.com
                        </span>
                      </div>
                      <div className="flex" data-id="element-6678">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6679">
                          Customer purchase IP
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6680">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex" data-id="element-6681">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6682">
                          Customer billing address
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6683">
                          Menara Maybank, Maybank Tower, 100 Jalan Tun Perak,
                          Bukit Bintang, 50050 Kuala Lumpur, Wilayah Persekutuan
                          Kuala Lumpur, Malaysia
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-8 py-4 flex justify-end" data-id="element-6684">
                <button onClick={() => setShowChallengeDetails(false)} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6685">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Help Modal */}
      {showHelpModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6686">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6687">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowHelpModal(false)} data-id="element-6688">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6689"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6690">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-6691">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-6692">
                <div className="flex items-start justify-between mb-6" data-id="element-6693">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6694">
                    {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
                  </h2>
                  <button onClick={() => setShowHelpModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-6695">
                    <CloseIcon size={24} data-id="element-6696" />
                  </button>
                </div>

                {isChallenged ? <div className="space-y-6" data-id="element-6697">
                    <div className="flex gap-4" data-id="element-6698">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6699">
                        1
                      </div>
                      <div className="flex-1" data-id="element-6700">
                        <p className="text-gray-700" data-id="element-6701">
                          The disputed amount will be credited back to your
                          wallet in the next settlement batch. You can view the
                          settlement details in the "Dispute settlement" section
                          of the payment details page.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-6702">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6703">
                        2
                      </div>
                      <div className="flex-1" data-id="element-6704">
                        <p className="text-gray-700" data-id="element-6705">
                          If you agree that the dispute reason is valid, it is
                          best to accept the dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-6706">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6707">
                        3
                      </div>
                      <div className="flex-1" data-id="element-6708">
                        <p className="text-gray-700" data-id="element-6709">
                          If the customer agrees to withdraw the dispute, you
                          still need to challenge the dispute and submit the
                          agreement as supporting evidence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-6710">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6711">
                        4
                      </div>
                      <div className="flex-1" data-id="element-6712">
                        <p className="text-gray-700" data-id="element-6713">
                          If the customer is upholding the dispute but you
                          believe it is not valid, consider whether you have
                          compelling evidence to challenge the dispute with.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-6714">
                      <p className="text-sm text-gray-600" data-id="element-6715">
                        Note that challenging the dispute can lead to higher
                        fees if the issuer is not convinced by your evidence.
                      </p>
                    </div>
                  </div> : <div className="space-y-6" data-id="element-6716">
                    <div className="flex gap-4" data-id="element-6717">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6718">
                        1
                      </div>
                      <div className="flex-1" data-id="element-6719">
                        <p className="text-gray-700" data-id="element-6720">
                          Review the dispute details and if necessary contact
                          your customer to understand why they filed the
                          dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-6721">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6722">
                        2
                      </div>
                      <div className="flex-1" data-id="element-6723">
                        <p className="text-gray-700" data-id="element-6724">
                          If you agree that the dispute reason is valid, it is
                          best to accept the dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-6725">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6726">
                        3
                      </div>
                      <div className="flex-1" data-id="element-6727">
                        <p className="text-gray-700" data-id="element-6728">
                          If the customer agrees to withdraw the dispute, you
                          still need to challenge the dispute and submit the
                          agreement as supporting evidence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-6729">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-6730">
                        4
                      </div>
                      <div className="flex-1" data-id="element-6731">
                        <p className="text-gray-700" data-id="element-6732">
                          If the customer is upholding the dispute but you
                          believe it is not valid, consider whether you have
                          compelling evidence to challenge the dispute with.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-6733">
                      <p className="text-sm text-gray-600" data-id="element-6734">
                        Note that challenging the dispute can lead to higher
                        fees if the issuer is not convinced by your evidence.
                      </p>
                    </div>
                  </div>}
              </div>

              <div className="bg-gray-50 px-8 py-4 flex justify-end" data-id="element-6735">
                <button onClick={() => setShowHelpModal(false)} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6736">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Accept Dispute Modal */}
      {showAcceptModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6737">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6738">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAcceptModal(false)} data-id="element-6739">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6740"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6741">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6742">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6743">
                <div className="flex items-start gap-4" data-id="element-6744">
                  <div className="flex-shrink-0" data-id="element-6745">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center" data-id="element-6746">
                      <AlertTriangleIcon size={24} className="text-orange-600" data-id="element-6747" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-6748">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-6749">
                      Accept this dispute?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-6750">
                      Accepting this dispute is final. Once you accept, the
                      customer will receive a refund of{' '}
                      {formatCurrency(dispute.amount)}. The funding timelines
                      can vary by the issuer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-6751">
                <button onClick={() => setShowAcceptModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6752">
                  Cancel
                </button>
                <button onClick={handleAcceptConfirm} className="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700" data-id="element-6753">
                  Accept dispute
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Add Note Modal */}
      {showAddNote && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6754">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6755">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAddNote(false)} data-id="element-6756">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6757"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6758">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-6759">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6760">
                <div className="flex items-start justify-between mb-4" data-id="element-6761">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-6762">
                    Add Note
                  </h2>
                  <button onClick={() => setShowAddNote(false)} className="text-gray-400 hover:text-gray-600" data-id="element-6763">
                    <CloseIcon size={20} data-id="element-6764" />
                  </button>
                </div>

                <div className="space-y-4" data-id="element-6765">
                  <div data-id="element-6766">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6767">
                      Note
                    </label>
                    <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={4} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Add a note for internal reference..." data-id="element-6768" />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4" data-id="element-6769">
                  <button onClick={() => setShowAddNote(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6770">
                    Cancel
                  </button>
                  <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6771">
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}